import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, Calendar, User, ExternalLink, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useSideTittel } from '@/hooks/useSideTittel';

const API = process.env.REACT_APP_BACKEND_URL;

const ArticleView = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [kommune, setKommune] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useSideTittel(
    article ? `${article.title}${kommune ? ` – ${kommune.kommune_name} kommune` : ""}` : "Artikkel",
    article?.description || (kommune ? `Sak om ${kommune.kommune_name} kommune frå Kommuneopprøret.` : undefined)
  );

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/articles/${articleId}`);
        setArticle(response.data);
        
        // Fetch kommune info
        if (response.data.kommune_code) {
          const kommuneResponse = await axios.get(`${API}/api/kommuner/${response.data.kommune_code}`);
          setKommune(kommuneResponse.data);
        }
      } catch (err) {
        setError('Kunne ikkje hente artikkelen');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryName = (categoryId) => {
    const categories = {
      'nyheter': 'Nyheiter',
      'budsjett': 'Budsjett og økonomi',
      'politikk': 'Politikk',
      'tjenester': 'Kommunale tenester',
      'utvikling': 'Utvikling og planar',
      'annet': 'Anna'
    };
    return categories[categoryId] || categoryId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#103766]"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-[#8AA4BF] mb-4">{error || 'Artikkelen blei ikkje funne'}</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Gå tilbake
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-[#4B647F] hover:text-[#103766]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake
            </Button>
            {kommune && (
              <Link
                to={`/kommune/${kommune.kommune_code}`}
                className="text-sm text-[#103766] hover:underline"
              >
                {kommune.kommune_name}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <article>
          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-[#E8F4F3] text-[#3F8A86]">
                <Tag className="w-3 h-3 mr-1" />
                {getCategoryName(article.category)}
              </Badge>
              {article.type === 'link' && (
                <Badge variant="outline" className="text-[#8AA4BF]">
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Ekstern lenkje
                </Badge>
              )}
            </div>
            
            <h1 className="text-3xl font-semibold text-[#1F2A36] mb-4">
              {article.title}
            </h1>
            
            <div className="flex items-center gap-4 text-sm text-[#8AA4BF]">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(article.published_at)}
              </span>
              {article.created_by_name && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {article.created_by_name}
                </span>
              )}
            </div>
          </div>

          {/* Article Body */}
          <Card className="bg-white border border-[#E6E9F0]">
            <CardContent className="pt-6">
              {article.type === 'link' ? (
                <div className="space-y-4">
                  {article.description && (
                    <p className="text-[#4B647F] leading-relaxed">
                      {article.description}
                    </p>
                  )}
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#103766] text-white rounded-lg hover:bg-[#0d2d52] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Åpne lenkje
                  </a>
                </div>
              ) : (
                <div 
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
                />
              )}
            </CardContent>
          </Card>
        </article>
      </main>
    </div>
  );
};

export default ArticleView;
